import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API } from '../App';
import { Shield, LogOut, Plus, FileCheck, Clock, CheckCircle2, XCircle, TrendingUp, Award, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checks, setChecks] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCheckDialog, setShowNewCheckDialog] = useState(false);
  const [showNewApplicantDialog, setShowNewApplicantDialog] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState('standard');
  const [showSCSDialog, setShowSCSDialog] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  
  const [applicantForm, setApplicantForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    country: 'Nigeria',
    consent_given: false,
    consent_signature: ''
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [checksRes, applicantsRes, packagesRes] = await Promise.all([
        axios.get(`${API}/checks`),
        axios.get(`${API}/applicants`),
        axios.get(`${API}/packages`)
      ]);
      setChecks(checksRes.data);
      setApplicants(applicantsRes.data);
      setPackages(packagesRes.data.packages);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleCreateApplicant = async (e) => {
    e.preventDefault();
    
    if (!applicantForm.consent_given) {
      toast.error('Applicant consent is required');
      return;
    }

    try {
      const response = await axios.post(`${API}/applicants`, applicantForm);
      toast.success('Applicant created successfully!');
      setShowNewApplicantDialog(false);
      setApplicants([...applicants, response.data]);
      setApplicantForm({
        full_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        address: '',
        city: '',
        state: '',
        country: 'Nigeria',
        consent_given: false,
        consent_signature: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create applicant');
    }
  };

  const handleCreateCheck = async () => {
    if (!selectedApplicant) {
      toast.error('Please select an applicant');
      return;
    }

    try {
      await axios.post(`${API}/checks`, {
        applicant_id: selectedApplicant,
        screening_package: selectedPackage,
        payment_method: 'paystack'
      });
      toast.success('Background check created successfully!');
      setShowNewCheckDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create check');
    }
  };

  const handleCompleteCheck = async (checkId) => {
    try {
      const response = await axios.post(`${API}/checks/${checkId}/complete`);
      toast.success('Check completed with SCS calculated!');
      loadData();
      
      // Show SCS dialog
      const check = checks.find(c => c.id === checkId);
      setSelectedCheck({...check, security_clearance_score: response.data.scs});
      setShowSCSDialog(true);
    } catch (error) {
      toast.error('Failed to complete check');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="text-blue-600" size={20} />;
      case 'pending':
        return <Clock className="text-yellow-600" size={20} />;
      case 'in_progress':
        return <Clock className="text-blue-600" size={20} />;
      case 'failed':
        return <XCircle className="text-red-600" size={20} />;
      default:
        return <Clock className="text-gray-600" size={20} />;
    }
  };

  const getSCSColor = (score) => {
    if (score >= 800) return 'text-blue-600';
    if (score >= 650) return 'text-blue-600';
    if (score >= 500) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSCSBand = (score) => {
    if (score >= 800) return 'Low Risk';
    if (score >= 650) return 'Moderate';
    if (score >= 500) return 'Caution';
    return 'High Risk';
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="dashboard" data-testid="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img src="/hakicheck-logo.png" alt="HakiCheck" className="header-logo" />
        </div>
        <div className="dashboard-user">
          <span className="user-name" data-testid="user-name">{user?.full_name}</span>
          <button className="logout-btn" data-testid="logout-btn" onClick={handleLogout}>
            <LogOut size={18} style={{ marginRight: '0.5rem', display: 'inline' }} />
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="dashboard-content">
        <div className="dashboard-welcome">
          <h1 className="welcome-title">Welcome back, {user?.full_name}!</h1>
          <p className="welcome-subtitle">Manage background verification with Security Clearance Scoring</p>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card primary">
            <Users size={24} className="stat-icon" />
            <div className="stat-label">Applicants</div>
            <div className="stat-value" data-testid="total-applicants">{applicants.length}</div>
          </div>
          <div className="stat-card info">
            <FileCheck size={24} className="stat-icon" />
            <div className="stat-label">Total Checks</div>
            <div className="stat-value" data-testid="total-checks">{checks.length}</div>
          </div>
          <div className="stat-card warning">
            <div className="stat-label">Pending</div>
            <div className="stat-value" data-testid="pending-checks">
              {checks.filter(c => c.status === 'pending' || c.status === 'in_progress').length}
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-label">Completed</div>
            <div className="stat-value" data-testid="completed-checks">
              {checks.filter(c => c.status === 'completed').length}
            </div>
          </div>
        </div>

        {/* Tabs for Applicants and Checks */}
        <Tabs defaultValue="checks" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="checks">Background Checks</TabsTrigger>
            <TabsTrigger value="applicants">Applicants</TabsTrigger>
          </TabsList>

          {/* Checks Tab */}
          <TabsContent value="checks">
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title">Background Checks</h2>
                <Dialog open={showNewCheckDialog} onOpenChange={setShowNewCheckDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn btn-primary" data-testid="new-check-btn">
                      <Plus size={20} style={{ marginRight: '0.5rem' }} />
                      New Check
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Request Background Check</DialogTitle>
                      <DialogDescription>
                        Select applicant and screening package
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6">
                      {/* Applicant Selection */}
                      <div>
                        <label className="block font-semibold mb-2">Select Applicant</label>
                        <select
                          className="w-full p-3 border rounded-lg"
                          value={selectedApplicant || ''}
                          onChange={(e) => setSelectedApplicant(e.target.value)}
                          data-testid="applicant-select"
                        >
                          <option value="">Choose an applicant...</option>
                          {applicants.map(applicant => (
                            <option key={applicant.id} value={applicant.id}>
                              {applicant.full_name} - {applicant.phone}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Screening Packages */}
                      <div>
                        <label className="block font-semibold mb-3">Choose Screening Package</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {packages.map(pkg => (
                            <div
                              key={pkg.type}
                              className={`package-card ${selectedPackage === pkg.type ? 'selected' : ''}`}
                              onClick={() => setSelectedPackage(pkg.type)}
                              data-testid={`package-${pkg.type}`}
                            >
                              <div className="package-header">
                                <h3 className="package-name">{pkg.name}</h3>
                                <div className="package-price">${pkg.price}</div>
                              </div>
                              <p className="package-description">{pkg.description}</p>
                              <ul className="package-features">
                                {pkg.features.map((feature) => (
                                  <li key={feature}>
                                    <CheckCircle2 size={16} className="text-blue-600" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Button 
                        onClick={handleCreateCheck}
                        className="w-full btn btn-primary"
                        data-testid="submit-check-btn"
                      >
                        Create Background Check
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Checks List */}
              {checks.length === 0 ? (
                <div className="empty-state" data-testid="empty-state">
                  <FileCheck size={48} style={{ color: '#6c757d', marginBottom: '1rem' }} />
                  <p>No background checks yet. Create your first one!</p>
                </div>
              ) : (
                <div className="checks-list">
                  {checks.map(check => (
                    <div key={check.id} className="check-item-enhanced" data-testid={`check-item-${check.id}`}>
                      <div className="check-header">
                        <div>
                          <h3>{applicants.find(a => a.id === check.applicant_id)?.full_name || 'Unknown'}</h3>
                          <span className="check-badge">{check.screening_package}</span>
                        </div>
                        <div className="check-status">
                          {getStatusIcon(check.status)}
                          <span>{check.status}</span>
                        </div>
                      </div>
                      
                      {check.security_clearance_score && (
                        <div className="scs-preview" onClick={() => {
                          setSelectedCheck(check);
                          setShowSCSDialog(true);
                        }}>
                          <div className="scs-score">
                            <Award className={getSCSColor(check.security_clearance_score.score)} size={32} />
                            <div>
                              <div className={`scs-value ${getSCSColor(check.security_clearance_score.score)}`}>
                                {check.security_clearance_score.score}
                              </div>
                              <div className="scs-band">{getSCSBand(check.security_clearance_score.score)}</div>
                            </div>
                          </div>
                          <div className="scs-confidence">
                            Confidence: {check.security_clearance_score.confidence}
                          </div>
                        </div>
                      )}
                      
                      <div className="check-actions">
                        {check.status === 'in_progress' && (
                          <Button
                            size="sm"
                            onClick={() => handleCompleteCheck(check.id)}
                            data-testid={`complete-check-${check.id}`}
                          >
                            Complete & Calculate SCS
                          </Button>
                        )}
                        {check.qr_code_url && (
                          <img src={check.qr_code_url} alt="Verification QR" className="qr-mini" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Applicants Tab */}
          <TabsContent value="applicants">
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 className="card-title">Applicants</h2>
                <Dialog open={showNewApplicantDialog} onOpenChange={setShowNewApplicantDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn btn-primary" data-testid="new-applicant-btn">
                      <Plus size={20} style={{ marginRight: '0.5rem' }} />
                      New Applicant
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Applicant</DialogTitle>
                      <DialogDescription>
                        Collect applicant information with consent
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateApplicant} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="form-group">
                          <label>Full Name *</label>
                          <input
                            type="text"
                            required
                            data-testid="applicant-name-input"
                            value={applicantForm.full_name}
                            onChange={(e) => setApplicantForm({...applicantForm, full_name: e.target.value})}
                            className="w-full p-2 border rounded"
                          />
                        </div>
                        <div className="form-group">
                          <label>Phone *</label>
                          <input
                            type="tel"
                            required
                            data-testid="applicant-phone-input"
                            value={applicantForm.phone}
                            onChange={(e) => setApplicantForm({...applicantForm, phone: e.target.value})}
                            className="w-full p-2 border rounded"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          data-testid="applicant-email-input"
                          value={applicantForm.email}
                          onChange={(e) => setApplicantForm({...applicantForm, email: e.target.value})}
                          className="w-full p-2 border rounded"
                        />
                      </div>

                      <div className="form-group">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          data-testid="applicant-dob-input"
                          value={applicantForm.date_of_birth}
                          onChange={(e) => setApplicantForm({...applicantForm, date_of_birth: e.target.value})}
                          className="w-full p-2 border rounded"
                        />
                      </div>

                      <div className="form-group">
                        <label>Address *</label>
                        <textarea
                          required
                          data-testid="applicant-address-input"
                          value={applicantForm.address}
                          onChange={(e) => setApplicantForm({...applicantForm, address: e.target.value})}
                          className="w-full p-2 border rounded"
                          rows="3"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="form-group">
                          <label>City</label>
                          <input
                            type="text"
                            value={applicantForm.city}
                            onChange={(e) => setApplicantForm({...applicantForm, city: e.target.value})}
                            className="w-full p-2 border rounded"
                          />
                        </div>
                        <div className="form-group">
                          <label>State</label>
                          <input
                            type="text"
                            value={applicantForm.state}
                            onChange={(e) => setApplicantForm({...applicantForm, state: e.target.value})}
                            className="w-full p-2 border rounded"
                          />
                        </div>
                        <div className="form-group">
                          <label>Country</label>
                          <input
                            type="text"
                            value={applicantForm.country}
                            onChange={(e) => setApplicantForm({...applicantForm, country: e.target.value})}
                            className="w-full p-2 border rounded"
                          />
                        </div>
                      </div>

                      {/* Consent Section */}
                      <div className="consent-section">
                        <div className="consent-box">
                          <h3>Consent for Background Verification</h3>
                          <p className="consent-text">
                            I hereby consent to HakiCheck conducting background verification checks including 
                            identity verification, criminal record checks, employment verification, and other 
                            checks as necessary. I understand my data will be processed in accordance with 
                            applicable data protection laws.
                          </p>
                          <div className="flex items-center space-x-2 mt-4">
                            <input
                              type="checkbox"
                              id="consent"
                              checked={applicantForm.consent_given}
                              onChange={(e) => setApplicantForm({...applicantForm, consent_given: e.target.checked})}
                              data-testid="consent-checkbox"
                            />
                            <label htmlFor="consent" className="cursor-pointer">
                              I consent to the above terms *
                            </label>
                          </div>
                        </div>
                      </div>

                      <Button type="submit" className="w-full btn btn-primary" data-testid="submit-applicant-btn">
                        Create Applicant
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {applicants.length === 0 ? (
                <div className="empty-state">
                  <Users size={48} style={{ color: '#6c757d', marginBottom: '1rem' }} />
                  <p>No applicants yet. Create one to start background checks.</p>
                </div>
              ) : (
                <div className="applicants-grid">
                  {applicants.map(applicant => (
                    <div key={applicant.id} className="applicant-card" data-testid={`applicant-${applicant.id}`}>
                      <h3>{applicant.full_name}</h3>
                      <p className="text-sm text-gray-600">{applicant.phone}</p>
                      <p className="text-sm text-gray-600">{applicant.email}</p>
                      <p className="text-sm mt-2">{applicant.address}</p>
                      <div className="mt-3 flex items-center text-xs text-blue-600">
                        <CheckCircle2 size={14} className="mr-1" />
                        Consent verified
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* SCS Details Dialog */}
      <Dialog open={showSCSDialog} onOpenChange={setShowSCSDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Security Clearance Score (SCS) Report</DialogTitle>
            <DialogDescription>
              Detailed breakdown of verification results
            </DialogDescription>
          </DialogHeader>
          
          {selectedCheck?.security_clearance_score && (
            <div className="scs-details">
              {/* Overall Score */}
              <div className="scs-overview">
                <div className="scs-main-score">
                  <Award className={getSCSColor(selectedCheck.security_clearance_score.score)} size={64} />
                  <div>
                    <div className={`scs-large ${getSCSColor(selectedCheck.security_clearance_score.score)}`}>
                      {selectedCheck.security_clearance_score.score}
                    </div>
                    <div className="scs-band-large">{getSCSBand(selectedCheck.security_clearance_score.score)}</div>
                    <div className="scs-confidence-large">
                      Confidence: {selectedCheck.security_clearance_score.confidence.toUpperCase()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contributing Factors */}
              <div className="contributing-factors">
                <h3>Contributing Factors</h3>
                {Object.entries(selectedCheck.security_clearance_score.contributing_factors || {}).map(([key, value]) => (
                  <div key={key} className="factor-item">
                    <div className="factor-header">
                      <span className="factor-name">{key.replace(/_/g, ' ').toUpperCase()}</span>
                      <span className="factor-score">{value.score}/100</span>
                    </div>
                    <Progress value={value.score} className="h-2 mb-2" />
                    <div className="factor-details">
                      {Object.entries(value.factors || {}).map(([factorKey, factorValue]) => (
                        <div key={factorKey} className="text-xs text-gray-600">
                          • {factorKey.replace(/_/g, ' ')}: {factorValue}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Recommendations */}
              <div className="recommendations">
                <h3>Recommendations</h3>
                <ul>
                  {selectedCheck.security_clearance_score.recommendations?.map((rec, idx) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ul>
              </div>

              {/* QR Code */}
              {selectedCheck.qr_code_url && (
                <div className="qr-section">
                  <h3>Verifiable Credential</h3>
                  <p className="text-sm text-gray-600 mb-3">Scan to verify this report</p>
                  <img src={selectedCheck.qr_code_url} alt="Verification QR" className="qr-code" />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        .loading-screen {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: #6c757d;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #6c757d;
        }

        .stat-icon {
          margin-bottom: 0.5rem;
          opacity: 0.7;
        }

        .checks-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .check-item-enhanced {
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          border: 1px solid #e9ecef;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .check-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .check-header h3 {
          font-size: 1.2rem;
          font-weight: 600;
          color: #212529;
          margin: 0 0 0.5rem 0;
        }

        .check-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #e9ecef;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .check-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .scs-preview {
          padding: 1rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%);
          border-radius: 10px;
          margin: 1rem 0;
          cursor: pointer;
          transition: transform 0.2s;
        }

        .scs-preview:hover {
          transform: translateY(-2px);
        }

        .scs-score {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .scs-value {
          font-size: 2rem;
          font-weight: 700;
        }

        .scs-band {
          font-size: 0.9rem;
          font-weight: 600;
          color: #6c757d;
        }

        .scs-confidence {
          margin-top: 0.5rem;
          font-size: 0.85rem;
          color: #6c757d;
        }

        .check-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 1rem;
        }

        .qr-mini {
          width: 60px;
          height: 60px;
        }

        .package-card {
          padding: 1.5rem;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .package-card:hover {
          border-color: #5a738f;
          transform: translateY(-4px);
        }

        .package-card.selected {
          border-color: #5a738f;
          background: #f0f9f4;
        }

        .package-header {
          margin-bottom: 1rem;
        }

        .package-name {
          font-size: 1.2rem;
          font-weight: 700;
          color: #212529;
          margin: 0 0 0.5rem 0;
        }

        .package-price {
          font-size: 1.5rem;
          font-weight: 700;
          color: #5a738f;
        }

        .package-description {
          color: #6c757d;
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }

        .package-features {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .package-features li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
          font-size: 0.85rem;
        }

        .applicants-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }

        .applicant-card {
          padding: 1.5rem;
          background: white;
          border-radius: 12px;
          border: 1px solid #e9ecef;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .applicant-card h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .consent-section {
          background: #f8f9fa;
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .consent-box h3 {
          margin: 0 0 1rem 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .consent-text {
          font-size: 0.85rem;
          line-height: 1.6;
          color: #495057;
        }

        .scs-details {
          padding: 1rem;
        }

        .scs-overview {
          text-align: center;
          padding: 2rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%);
          border-radius: 12px;
          margin-bottom: 2rem;
        }

        .scs-main-score {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 2rem;
        }

        .scs-large {
          font-size: 4rem;
          font-weight: 700;
        }

        .scs-band-large {
          font-size: 1.5rem;
          font-weight: 600;
          color: #6c757d;
        }

        .scs-confidence-large {
          font-size: 1rem;
          color: #6c757d;
          margin-top: 0.5rem;
        }

        .contributing-factors {
          margin-bottom: 2rem;
        }

        .contributing-factors h3 {
          margin-bottom: 1rem;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .factor-item {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .factor-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
        }

        .factor-name {
          font-weight: 600;
          font-size: 0.9rem;
        }

        .factor-score {
          font-weight: 700;
          color: #5a738f;
        }

        .recommendations {
          margin-bottom: 2rem;
        }

        .recommendations h3 {
          margin-bottom: 1rem;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .recommendations ul {
          list-style: none;
          padding: 0;
        }

        .recommendations li {
          padding: 0.75rem 1rem;
          background: #f0f9f4;
          border-left: 4px solid #5a738f;
          margin-bottom: 0.5rem;
          border-radius: 4px;
        }

        .qr-section {
          text-align: center;
          padding: 2rem;
          background: white;
          border: 2px dashed #e9ecef;
          border-radius: 12px;
        }

        .qr-section h3 {
          margin-bottom: 0.5rem;
          font-size: 1.1rem;
          font-weight: 600;
        }

        .qr-code {
          max-width: 200px;
          margin: 0 auto;
        }
      `}</style>
    </div>
  );
}

export default Dashboard;
